import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Phone, Mail, ChevronDown, Check, Loader2, Globe } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SERIF = "'Playfair Display', Georgia, serif";

const nextBusinessDays = (n = 5) => {
  const days = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (days.length < n) {
    if (d.getDay() !== 0 && d.getDay() !== 6) days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
};

const fmtDay = (d) => d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });

const Field = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">{label}</label>
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testId}
      className="w-full bg-white/[0.04] border border-white/10 rounded-md px-3 py-2.5 text-[15px] text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-sky-700/60 transition-colors" />
  </div>
);

function CallbackForm({ slug, recipientName }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(recipientName || '');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [when, setWhen] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/public/landing/${slug}/lead`, {
        type: 'callback', name, company, phone, email, preferred_time: when,
      }, { withCredentials: false });
      setDone(true);
    } catch { /* silent */ }
    setSending(false);
  };

  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} data-testid="landing-callback-toggle"
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/[0.03] transition-colors">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Ich melde mich bei Ihnen</div>
          <div className="text-lg text-slate-100 mt-0.5" style={{ fontFamily: SERIF }}>Rückruf anfordern</div>
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3">
          {done ? (
            <div className="flex items-center gap-2 text-emerald-400 text-sm py-3" data-testid="landing-callback-success">
              <Check className="w-4 h-4" /> Vielen Dank — ich melde mich bei Ihnen!
            </div>
          ) : (
            <>
              <Field label="Ihr Name" value={name} onChange={setName} testId="landing-callback-name" />
              <Field label="Firma" value={company} onChange={setCompany} testId="landing-callback-company" />
              <Field label="Telefon" value={phone} onChange={setPhone} testId="landing-callback-phone" />
              <Field label="E-Mail (optional)" value={email} onChange={setEmail} testId="landing-callback-email" />
              <Field label="Wann passt es Ihnen?" value={when} onChange={setWhen} placeholder="z.B. morgen Vormittag" testId="landing-callback-when" />
              <button onClick={submit} disabled={sending || !name.trim()} data-testid="landing-callback-submit"
                className="w-full mt-1 bg-sky-800/70 hover:bg-sky-700/70 disabled:opacity-40 text-slate-100 rounded-md py-3 text-sm tracking-wide transition-colors">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Rückruf anfordern'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function AppointmentForm({ slug, recipientName, onOpen }) {
  const [open, setOpen] = useState(false);
  const [slotsData, setSlotsData] = useState(undefined);
  const [day, setDay] = useState(null);
  const [time, setTime] = useState(null);
  const [name, setName] = useState(recipientName || '');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');

  const loadSlots = () => {
    setSlotsData(undefined);
    axios.get(`${API}/api/public/landing/${slug}/slots`, { withCredentials: false })
      .then(r => setSlotsData(r.data))
      .catch(() => setSlotsData({ available: false }));
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      onOpen?.();
      if (slotsData === undefined) loadSlots();
    }
  };

  const bookable = slotsData?.available;
  const days = bookable ? slotsData.days.filter(d => d.slots.length > 0) : [];
  const fallbackDays = nextBusinessDays(5);
  const fallbackTimes = ['09:00', '11:00', '14:00', '16:00'];

  const emailOk = /\S+@\S+\.\S+/.test(email);

  const submit = async () => {
    if (!name.trim() || !day || !time) return;
    if (bookable && !emailOk) return;
    setSending(true);
    setError('');
    try {
      if (bookable) {
        const r = await axios.post(`${API}/api/public/landing/${slug}/book`, {
          start: `${day.date}T${time}`, name, company, phone, email,
        }, { withCredentials: false });
        setDone({ booked: true, when: r.data.when, invited: r.data.invited });
      } else {
        await axios.post(`${API}/api/public/landing/${slug}/lead`, {
          type: 'appointment', name, company, phone,
          preferred_time: `${fmtDay(day)} um ${time} Uhr`,
        }, { withCredentials: false });
        setDone({ booked: false });
      }
    } catch (e) {
      if (e.response?.status === 409) {
        setError(e.response?.data?.detail || 'Dieser Termin ist nicht mehr frei.');
        setTime(null);
        loadSlots();
      } else {
        setError('Etwas hat nicht geklappt — bitte versuchen Sie es erneut oder nutzen Sie den Rückruf.');
      }
    }
    setSending(false);
  };

  const chip = (active) =>
    `px-3 py-2 rounded-md text-[13px] border transition-colors ${active
      ? 'bg-sky-800/60 border-sky-600/50 text-slate-100'
      : 'bg-white/[0.03] border-white/10 text-slate-400 hover:border-white/25'}`;

  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden">
      <button onClick={toggle} data-testid="landing-appointment-toggle"
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/[0.03] transition-colors">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Freien Zeitpunkt wählen</div>
          <div className="text-lg text-slate-100 mt-0.5" style={{ fontFamily: SERIF }}>Termin vereinbaren</div>
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4">
          {done ? (
            <div className="flex items-start gap-2 text-emerald-400 text-sm py-3" data-testid="landing-appointment-success">
              <Check className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                {done.booked
                  ? <>Ihr Termin am <b>{done.when}</b> ist fest eingetragen.{done.invited ? ' Sie erhalten in Kürze eine Kalendereinladung per E-Mail.' : ' Ich rufe Sie zum Termin an.'} Ich freue mich auf das Gespräch mit Ihnen!</>
                  : 'Vielen Dank — Ihre Terminanfrage ist angekommen, wir bestätigen kurzfristig.'}
              </span>
            </div>
          ) : slotsData === undefined ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-sky-700 animate-spin" /></div>
          ) : (
            <>
              {bookable && (
                <p className="text-[11px] text-slate-500">
                  Verfügbare Zeiten live aus dem Kalender · Dauer ca. {slotsData.duration} Minuten
                </p>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Tag</div>
                <div className="flex flex-wrap gap-2">
                  {bookable
                    ? days.map(d => (
                      <button key={d.date} onClick={() => { setDay(d); setTime(null); }} className={chip(day?.date === d.date)}>
                        {d.label}
                      </button>
                    ))
                    : fallbackDays.map(d => (
                      <button key={d.toISOString()} onClick={() => setDay(d)} className={chip(day?.getTime?.() === d.getTime())}>
                        {fmtDay(d)}
                      </button>
                    ))}
                  {bookable && days.length === 0 && (
                    <span className="text-xs text-slate-500">Aktuell keine freien Zeiten — bitte nutzen Sie den Rückruf-Weg.</span>
                  )}
                </div>
              </div>
              {day && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Uhrzeit</div>
                  <div className="flex flex-wrap gap-2">
                    {(bookable ? day.slots : fallbackTimes).map(t => (
                      <button key={t} onClick={() => setTime(t)} className={chip(time === t)}>{t}</button>
                    ))}
                  </div>
                </div>
              )}
              <Field label="Ihr Name" value={name} onChange={setName} testId="landing-appointment-name" />
              {bookable && (
                <Field label="E-Mail (für die Kalendereinladung)" value={email} onChange={setEmail} testId="landing-appointment-email" />
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Firma" value={company} onChange={setCompany} testId="landing-appointment-company" />
                <Field label="Telefon (optional)" value={phone} onChange={setPhone} testId="landing-appointment-phone" />
              </div>
              {error && <p className="text-[12px] text-amber-400" data-testid="landing-appointment-error">{error}</p>}
              <button onClick={submit} disabled={sending || !name.trim() || !day || !time || (bookable && !emailOk)} data-testid="landing-appointment-submit"
                className="w-full bg-sky-800/70 hover:bg-sky-700/70 disabled:opacity-40 text-slate-100 rounded-md py-3 text-sm tracking-wide transition-colors">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : bookable ? 'Termin verbindlich buchen' : 'Termin anfragen'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function PublicLandingPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const playTracked = useRef({ audio: false, video: false });
  const isPreview = new URLSearchParams(window.location.search).get('preview') === '1';

  const trackPlay = (kind) => {
    if (isPreview || playTracked.current[kind]) return;
    playTracked.current[kind] = true;
    axios.post(`${API}/api/public/landing/${slug}/play`, { kind }, { withCredentials: false }).catch(() => {});
  };

  // engagement tracking: time on page, media progress, contact taps (per session)
  const sidRef = useRef('');
  const engRef = useRef({ seconds: 0, videoSec: 0, videoPct: 0, audioSec: 0, audioPct: 0, scrollPct: 0, clicks: {}, dirty: false });

  const sendEngagement = (immediate = false) => {
    const e = engRef.current;
    if (isPreview || !sidRef.current || (!e.dirty && !immediate)) return;
    e.dirty = false;
    const payload = JSON.stringify({
      sid: sidRef.current,
      seconds_on_page: Math.round(e.seconds),
      video_seconds: Math.round(e.videoSec * 10) / 10,
      video_pct: Math.round(e.videoPct * 10) / 10,
      audio_seconds: Math.round(e.audioSec * 10) / 10,
      audio_pct: Math.round(e.audioPct * 10) / 10,
      scroll_pct: Math.round(e.scrollPct * 10) / 10,
      clicks: e.clicks,
    });
    const url = `${API}/api/public/landing/${slug}/engagement`;
    try {
      // plain string payload = CORS-safelisted → iOS Safari never rejects it
      if (!navigator.sendBeacon(url, payload)) throw new Error('beacon rejected');
    } catch {
      try { fetch(url, { method: 'POST', body: payload, keepalive: true }); } catch { /* ignore */ }
    }
  };

  const onMediaTime = (kind) => (e) => {
    const el = e.target;
    if (isPreview || !el?.duration) return;
    const eng = engRef.current;
    const pct = Math.min(100, (el.currentTime / el.duration) * 100);
    if (kind === 'video') {
      eng.videoSec = Math.max(eng.videoSec, el.currentTime);
      eng.videoPct = Math.max(eng.videoPct, pct);
    } else {
      eng.audioSec = Math.max(eng.audioSec, el.currentTime);
      eng.audioPct = Math.max(eng.audioPct, pct);
    }
    eng.dirty = true;
  };

  const [optOutOpen, setOptOutOpen] = useState(false);
  const [optOutReason, setOptOutReason] = useState('');
  const [optOutNote, setOptOutNote] = useState('');
  const [optOutSent, setOptOutSent] = useState(false);

  const OPTOUT_REASONS = [
    { value: 'solved', label: 'Meine Nachfolge ist bereits gelöst' },
    { value: 'in_talks', label: 'Ich bin bereits in Gesprächen mit anderen Interessenten' },
    { value: 'no_sale', label: 'Ein Verkauf kommt für mich grundsätzlich nicht in Frage' },
    { value: 'timing', label: 'Der Zeitpunkt passt nicht' },
    { value: 'other', label: 'Sonstiges' },
  ];

  const postOptOut = (reason, note) => {
    if (isPreview) return;
    try {
      fetch(`${API}/api/public/landing/${slug}/opt-out`, {
        method: 'POST', body: JSON.stringify({ sid: sidRef.current, reason, note }), keepalive: true,
      }).catch(() => {});
    } catch { /* ignore */ }
  };

  const handleOptOutClick = () => {
    postOptOut('', '');
    setOptOutOpen(true);
  };

  const handleOptOutReasonSubmit = () => {
    if (!optOutReason) return;
    postOptOut(optOutReason, optOutNote);
    setOptOutSent(true);
  };

  const trackClick = (k) => {
    if (isPreview) return;
    engRef.current.clicks[k] = true;
    engRef.current.dirty = true;
    sendEngagement(true);
  };

  useEffect(() => {
    if (isPreview) return undefined;
    sidRef.current = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const tick = setInterval(() => {
      if (document.visibilityState === 'visible') {
        engRef.current.seconds += 1;
        engRef.current.dirty = true;
      }
    }, 1000);
    const flush = setInterval(() => sendEngagement(false), 10000);
    // first beacon after 3s so even a quick peek creates the session
    const early = setTimeout(() => sendEngagement(true), 3000);
    const onVis = () => { if (document.visibilityState === 'hidden') sendEngagement(true); };
    const onHide = () => sendEngagement(true);
    const onScroll = () => {
      const doc = document.documentElement;
      const denom = doc.scrollHeight - window.innerHeight;
      const pct = denom > 0 ? Math.min(100, ((window.scrollY || doc.scrollTop || 0) / denom) * 100) : 100;
      if (pct > engRef.current.scrollPct) {
        engRef.current.scrollPct = pct;
        engRef.current.dirty = true;
      }
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', onHide);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearInterval(tick); clearInterval(flush); clearTimeout(early);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('scroll', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    // preview=1 (opened from the app) must reach the API too — otherwise the open would count as a scan
    axios.get(`${API}/api/public/landing/${slug}`, { withCredentials: false, params: isPreview ? { preview: '1' } : undefined })
      .then(r => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const toggleAudio = () => {
    if (!data?.has_audio || !audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
      trackPlay('audio');
    }
  };

  const toggleVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    if (videoPlaying) {
      v.pause();
      setVideoPlaying(false);
    } else {
      v.play();
      setVideoPlaying(true);
      trackPlay('video');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A141F' }}>
        <Loader2 className="w-6 h-6 text-sky-700 animate-spin" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" style={{ background: '#0A141F' }}>
        <div className="text-slate-100 font-bold text-2xl tracking-wide">OLTA<span className="block text-[10px] tracking-[0.35em] font-normal text-slate-400">NACHFOLGE</span></div>
        <p className="text-slate-400 mt-8" style={{ fontFamily: SERIF }} data-testid="landing-not-found">Diese Seite ist nicht mehr verfügbar.</p>
      </div>
    );
  }

  const sender = data.sender || {};
  const photoUrl = sender.has_photo ? `${API}/api/public/landing/${slug}/photo` : null;
  const videoUrl = data.has_video ? `${API}/api/public/landing/${slug}/video#t=0.1` : null;
  const audioUrl = data.has_audio ? `${API}/api/public/landing/${slug}/audio?v=${encodeURIComponent(data.audio_version)}` : null;

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ background: '#0A141F' }} data-testid="public-landing-page">
      <style>{`
        @keyframes ringPulse { 0%,100% { box-shadow: 0 0 45px 5px rgba(56,130,246,0.25); } 50% { box-shadow: 0 0 75px 15px rgba(56,130,246,0.45); } }
        .olta-ring { animation: ringPulse 4s ease-in-out infinite; }
        .olta-ring-playing { animation: ringPulse 1.4s ease-in-out infinite; }
      `}</style>
      {/* atmosphere */}
      <div className="pointer-events-none absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 45% at 50% 0%, rgba(30,58,95,0.35), transparent 65%), radial-gradient(ellipse 70% 40% at 50% 100%, rgba(15,35,60,0.5), transparent 70%)',
      }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
      }} />

      <div className="relative z-10 max-w-2xl mx-auto px-6 pb-16 pt-4">
        {/* hero */}
        <div className="flex flex-col items-center text-center mt-10 sm:mt-14">
          <h1 className="text-slate-100 text-3xl sm:text-4xl leading-snug mt-4 mb-8" style={{ fontFamily: SERIF }} data-testid="landing-headline">
            {data.salutation
              ? (data.salutation.startsWith('Eheleute') ? `Sehr geehrte ${data.salutation},` : `Guten Tag, ${data.salutation}!`)
              : 'Guten Tag!'}
          </h1>

          {videoUrl ? (
            <button onClick={toggleVideo} data-testid="landing-video-block"
              className={`relative w-56 h-56 sm:w-64 sm:h-64 rounded-full border-[5px] border-sky-800/70 p-1.5 ${videoPlaying ? 'olta-ring-playing' : 'olta-ring'} transition-transform active:scale-[0.98]`}>
              <video ref={videoRef} playsInline preload="metadata" poster={photoUrl || undefined}
                onTimeUpdate={onMediaTime('video')}
                onEnded={() => { setVideoPlaying(false); if (!isPreview) { engRef.current.videoPct = 100; engRef.current.dirty = true; sendEngagement(true); } }}
                className="w-full h-full rounded-full object-cover object-top bg-white pointer-events-none"
                data-testid="landing-video">
                <source src={videoUrl} />
              </video>
              <div className="absolute bottom-1 right-1 w-11 h-11 rounded-full bg-sky-800 border border-sky-600/60 flex items-center justify-center shadow-lg" data-testid="landing-video-play">
                {videoPlaying ? (
                  <span className="flex items-end gap-[3px] h-4">
                    <span className="w-[3px] bg-slate-100 animate-pulse" style={{ height: '60%' }} />
                    <span className="w-[3px] bg-slate-100 animate-pulse" style={{ height: '100%', animationDelay: '0.2s' }} />
                    <span className="w-[3px] bg-slate-100 animate-pulse" style={{ height: '75%', animationDelay: '0.4s' }} />
                  </span>
                ) : (
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-slate-100 ml-0.5"><path d="M8 5v14l11-7z" /></svg>
                )}
              </div>
            </button>
          ) : (
            <button onClick={toggleAudio} data-testid="landing-photo-button"
              className={`relative w-56 h-56 sm:w-64 sm:h-64 rounded-full border-[5px] border-sky-800/70 p-1.5 ${playing ? 'olta-ring-playing' : 'olta-ring'} transition-transform active:scale-[0.98]`}>
              {photoUrl ? (
                <img src={photoUrl} alt={sender.name} className="w-full h-full rounded-full object-cover object-top" />
              ) : (
                <div className="w-full h-full rounded-full bg-sky-950 flex items-center justify-center text-5xl text-sky-300" style={{ fontFamily: SERIF }}>
                  {(sender.name || '?').charAt(0)}
                </div>
              )}
              {data.has_audio && (
                <div className="absolute bottom-1 right-1 w-11 h-11 rounded-full bg-sky-800 border border-sky-600/60 flex items-center justify-center shadow-lg">
                  {playing ? (
                    <span className="flex items-end gap-[3px] h-4">
                      <span className="w-[3px] bg-slate-100 animate-pulse" style={{ height: '60%' }} />
                      <span className="w-[3px] bg-slate-100 animate-pulse" style={{ height: '100%', animationDelay: '0.2s' }} />
                      <span className="w-[3px] bg-slate-100 animate-pulse" style={{ height: '75%', animationDelay: '0.4s' }} />
                    </span>
                  ) : (
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-slate-100 ml-0.5"><path d="M8 5v14l11-7z" /></svg>
                  )}
                </div>
              )}
            </button>
          )}
          {audioUrl && <audio ref={audioRef} src={audioUrl} onTimeUpdate={onMediaTime('audio')}
            onEnded={() => { setPlaying(false); if (!isPreview) { engRef.current.audioPct = 100; engRef.current.dirty = true; sendEngagement(true); } }} preload="none" />}

          <p className="italic text-slate-300 text-2xl sm:text-3xl leading-snug mt-8" style={{ fontFamily: SERIF }}>
            {videoUrl
              ? 'Ich habe eine persönliche Videobotschaft für Sie — und würde mich sehr freuen, mit Ihnen ins Gespräch zu kommen.'
              : data.has_audio
                ? 'Tippen Sie auf mein Bild — ich erzähle Ihnen kurz, wer ich bin.'
                : 'Ich würde mich sehr freuen, mit Ihnen ins Gespräch zu kommen.'}
          </p>
        </div>

        {/* contact card */}
        <div className="mt-14 sm:mt-16 rounded-2xl border border-white/[0.06] p-5 sm:p-7 space-y-3" style={{ background: 'rgba(13,26,40,0.6)', backdropFilter: 'blur(14px)' }}>
          <h2 className="text-center text-slate-200 text-xl sm:text-2xl leading-snug mb-5" style={{ fontFamily: SERIF }}>
            So erreichen Sie mich:
          </h2>

          {sender.phone && (
            <a href={`tel:${sender.phone.replace(/\s/g, '')}`} data-testid="landing-call-link" onClick={() => trackClick('phone')}
              className="flex items-center justify-between bg-white/[0.03] border border-white/[0.06] rounded-xl px-5 py-4 hover:bg-white/[0.05] transition-colors">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Anrufen</div>
                <div className="text-lg text-slate-100 mt-0.5" style={{ fontFamily: SERIF }}>{sender.phone}</div>
              </div>
              <Phone className="w-5 h-5 text-slate-500" />
            </a>
          )}

          <CallbackForm slug={slug} recipientName={data.recipient_name} />

          {sender.email && (
            <a href={`mailto:${sender.email}?subject=${encodeURIComponent(`Vertrauliche Anfrage — ${data.company_name || data.slug}`)}`} data-testid="landing-email-link" onClick={() => trackClick('email')}
              className="flex items-center justify-between bg-white/[0.03] border border-white/[0.06] rounded-xl px-5 py-4 hover:bg-white/[0.05] transition-colors">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Schreiben</div>
                <div className="text-lg text-slate-100 mt-0.5 break-all" style={{ fontFamily: SERIF }}>{sender.email}</div>
              </div>
              <Mail className="w-5 h-5 text-slate-500 shrink-0 ml-3" />
            </a>
          )}

          <AppointmentForm slug={slug} recipientName={data.recipient_name} onOpen={() => trackClick('calendar')} />

          {sender.website_url && (
            <a href={sidRef.current ? `${sender.website_url}${sender.website_url.includes('?') ? '&' : '?'}olta=${sidRef.current}` : sender.website_url}
              target="_blank" rel="noopener noreferrer" data-testid="landing-website-link" onClick={() => trackClick('website')}
              className="flex items-center justify-between bg-gradient-to-r from-white/[0.06] to-white/[0.03] border border-white/[0.14] rounded-xl px-5 py-4 hover:border-white/[0.28] hover:from-white/[0.09] transition-all">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Mehr über uns erfahren</div>
                <div className="text-lg text-slate-50 mt-0.5" style={{ fontFamily: SERIF }}>
                  {sender.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </div>
              </div>
              <Globe className="w-5 h-5 text-slate-300 shrink-0 ml-3" />
            </a>
          )}
        </div>

        {/* opt-out */}
        <div className="text-center mt-12">
          <button onClick={handleOptOutClick} data-testid="landing-optout-btn"
            className="text-[15px] font-medium text-red-200 bg-red-900/40 hover:bg-red-800/50 border border-red-700/60 hover:border-red-500/70 rounded-full px-7 py-3 transition-colors">
            Bitte kontaktieren Sie mich nicht mehr.
          </button>
        </div>

        {/* footer */}
        <div className="text-center mt-12 space-y-6">
          <p className="text-slate-500 italic text-[15px] leading-relaxed" style={{ fontFamily: SERIF }}>
            Alle Gespräche sind vertraulich.<br />Nichts verlässt diesen Kreis ohne Ihre Zustimmung.
          </p>
          <p className="text-[11px] text-slate-600 tracking-wide">olta-nachfolge.com &nbsp;·&nbsp; © {new Date().getFullYear()} OLTA Nachfolge</p>
        </div>
      </div>

      {optOutOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(4,10,18,0.85)', backdropFilter: 'blur(6px)' }} data-testid="landing-optout-modal">
          <div className="w-full max-w-md rounded-2xl border border-white/[0.08] p-6 sm:p-7 max-h-[90vh] overflow-y-auto" style={{ background: '#0d1a28' }}>
            {optOutSent ? (
              <p className="text-slate-100 text-xl leading-snug" style={{ fontFamily: SERIF }} data-testid="landing-optout-thanks">
                Vielen Dank für Ihre Rückmeldung — wir wünschen Ihnen alles Gute!
              </p>
            ) : (
              <>
                <p className="text-slate-100 text-xl leading-snug" style={{ fontFamily: SERIF }}>Sie sind nun abgemeldet. Schade!</p>
                <p className="text-slate-400 text-sm mt-2">Wir werden Sie nicht erneut kontaktieren.</p>
                <p className="text-slate-300 text-sm mt-5 leading-relaxed">
                  Wir würden uns allerdings sehr freuen zu erfahren, warum — das ist völlig optional, würde uns aber sehr helfen:
                </p>
                <div className="mt-3 space-y-2">
                  {OPTOUT_REASONS.map(r => (
                    <button key={r.value} onClick={() => setOptOutReason(r.value)} data-testid={`landing-optout-reason-${r.value}`}
                      className={`w-full text-left text-sm rounded-xl border px-4 py-2.5 transition-colors ${optOutReason === r.value ? 'border-slate-300 bg-white/[0.08] text-slate-100' : 'border-white/[0.08] bg-white/[0.02] text-slate-300 hover:bg-white/[0.05]'}`}>
                      {r.label}
                    </button>
                  ))}
                </div>
                {optOutReason === 'other' && (
                  <textarea value={optOutNote} onChange={e => setOptOutNote(e.target.value)} rows={2}
                    placeholder="Ihre Anmerkung (optional)" data-testid="landing-optout-note"
                    className="mt-2 w-full rounded-xl border border-white/[0.08] bg-white/[0.02] text-slate-200 text-sm px-4 py-2.5 focus:outline-none focus:border-slate-400 placeholder:text-slate-600" />
                )}
                <button onClick={handleOptOutReasonSubmit} disabled={!optOutReason} data-testid="landing-optout-submit"
                  className="mt-4 w-full rounded-xl bg-slate-200 text-slate-900 text-sm font-medium py-2.5 disabled:opacity-40 hover:bg-white transition-colors">
                  Absenden
                </button>
              </>
            )}
            <button onClick={() => setOptOutOpen(false)} data-testid="landing-optout-close"
              className="mt-3 w-full text-center text-[13px] text-slate-500 hover:text-slate-300 py-1.5 transition-colors">
              Schließen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
